import { useMemo, useState } from "react";
import { ChevronRight, Sparkles, Square, Triangle } from "lucide-react";
import { Card, CardContent } from "../ui/Card";
import Thumbnail from "../common/Thumbnail";
import ScreenshotViewer from "../common/ScreenshotViewer";
import { enrichAffectedItems, resolveIssueSeverity } from "../../utils/enrichAffectedResources";
import { resolveAiSuggestionIssue } from "../../utils/resolveAiSuggestionIssue";
import {
  issueCardOpenShellClass,
  issueCardBodyClass,
  issueCardCollapsedClass,
} from "../../utils/stickyIssueHeader";
import StickyIssueHeader from "../common/StickyIssueHeader";
import { hasLighthouseIssueContent } from "../metrics/performance/performanceIssueHelpers";
import PerformanceIssueContent from "../metrics/performance/PerformanceIssueContent";
import { isSocialMetaRelatedIssue } from "../../utils/socialMetaIssueHint";

const IMAGE_PATH =
  /(\.(avif|webp|png|jpe?g|gif|svg))(\?[^)\s"']*)?($|\)|"|'|\s)/i;

function extractImagePath(text) {
  if (!text) return null;
  const src = text.match(/\bsrc=["']([^"']+)["']/i)?.[1];
  if (src && IMAGE_PATH.test(src)) return src;

  const paren = text.match(
    /\(([a-zA-Z0-9_./%-]+?\.(?:avif|webp|png|jpe?g|gif|svg)[^)]*)\)/i,
  )?.[1];
  if (paren) return paren;

  const trimmed = text.trim();
  if (IMAGE_PATH.test(trimmed) && !trimmed.includes("<")) return trimmed;

  return null;
}

function resolvePageUrl(path, pageUrl) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  if (!pageUrl) return null;
  try {
    return new URL(path, pageUrl).href;
  } catch {
    return null;
  }
}

function extractExternalUrl(text) {
  if (!text) return null;
  return text.match(/https?:\/\/[^\s"'<>]+/i)?.[0] || null;
}

function isImageResource(url, node, text) {
  if (url && IMAGE_PATH.test(url)) return true;
  if (node?.snippet && /<img\b/i.test(node.snippet)) return true;
  if (extractImagePath(text)) return true;
  return false;
}

function IssueSeverityIcon({ severity = "needs-improvement", className = "" }) {
  if (severity === "poor") {
    return (
      <Triangle
        size={16}
        className={`text-red-500 flex-shrink-0 ${className}`}
        aria-hidden
      />
    );
  }
  return (
    <Square
      size={16}
      className={`text-orange-500 flex-shrink-0 ${className}`}
      aria-hidden
    />
  );
}

function BoxedListItem({ children, mono = false }) {
  return (
    <div
      className={`rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm leading-relaxed text-muted-foreground ${
        mono ? "font-mono text-xs break-all text-foreground" : ""
      }`}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
      {children}
    </p>
  );
}

function AffectedItem({
  item,
  pageUrl,
  screenshot,
  strategy,
  onThumbnailClick,
}) {
  const [imgError, setImgError] = useState(false);
  const text = typeof item === "string" ? item : item.text;
  const lighthouseUrl = typeof item === "object" ? item.url : null;
  const node = typeof item === "object" ? item.node : null;

  const imagePath = extractImagePath(text);
  const resourceHref =
    lighthouseUrl ||
    resolvePageUrl(imagePath, pageUrl) ||
    extractExternalUrl(text);

  const isImage = isImageResource(resourceHref, node, text);

  const hasScreenshotThumb =
    isImage &&
    screenshot &&
    node?.boundingRect &&
    node.boundingRect.width > 0 &&
    node.boundingRect.height > 0;

  const content = (
    <span className="min-w-0 break-all group-hover:text-brand transition-colors">
      {text}
    </span>
  );

  if (hasScreenshotThumb) {
    return (
      <BoxedListItem mono>
        <div className="flex items-start gap-3 min-w-0">
          <Thumbnail
            screenshot={screenshot}
            rect={node.boundingRect}
            strategy={strategy}
            onClick={() => onThumbnailClick?.(node.boundingRect)}
          />
          <div className="min-w-0 flex-1 space-y-1.5">
            {resourceHref ? (
              <a
                href={resourceHref}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:text-brand transition-colors"
                title="Open image in a new tab"
              >
                {content}
              </a>
            ) : (
              content
            )}
          </div>
        </div>
      </BoxedListItem>
    );
  }

  if (resourceHref && isImage) {
    return (
      <BoxedListItem mono>
        <a
          href={resourceHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-3 group min-w-0"
          title="Open image in a new tab"
        >
          {!imgError ? (
            <img
              src={resourceHref}
              alt=""
              className="w-14 h-14 shrink-0 rounded-md border border-border object-cover bg-muted"
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
            />
          ) : null}
          {content}
        </a>
      </BoxedListItem>
    );
  }

  if (resourceHref) {
    return (
      <BoxedListItem mono>
        <a
          href={resourceHref}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all hover:text-brand transition-colors"
          title="Open resource in a new tab"
        >
          {text}
        </a>
      </BoxedListItem>
    );
  }

  return <BoxedListItem mono>{text}</BoxedListItem>;
}

function AffectedList({
  items,
  pageUrl,
  screenshot,
  strategy,
  onThumbnailClick,
}) {
  if (!items?.length) return null;
  return (
    <div className="mt-3">
      <SectionLabel>Affected</SectionLabel>
      <div className="space-y-2">
        {items.map((item, i) => (
          <AffectedItem
            key={i}
            item={item}
            pageUrl={pageUrl}
            screenshot={screenshot}
            strategy={strategy}
            onThumbnailClick={onThumbnailClick}
          />
        ))}
      </div>
    </div>
  );
}

const PARTYTOWN_URL = "https://partytown.qwik.dev/";

function renderFixStepContent(step) {
  if (typeof step !== "string") return step;
  if (!step.includes("Partytown")) return step;

  return step.split(/(Partytown)/g).map((part, i) =>
    part === "Partytown" ? (
      <a
        key={i}
        href={PARTYTOWN_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand font-medium hover:underline"
      >
        Partytown
      </a>
    ) : (
      part
    ),
  );
}

function FixSteps({ steps, contextual = false, issueKey, issueTitle, onOpenSocialTab }) {
  if (!steps?.length) return null;

  const showSocialHint =
    contextual && isSocialMetaRelatedIssue(issueKey, issueTitle);

  return (
    <div className="mt-4 rounded-xl bg-brand/[0.05] px-3 py-3 sm:px-4 sm:py-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-brand shrink-0" aria-hidden />
        <p className="text-xs font-semibold uppercase tracking-wide text-brand">
          {contextual ? "Recommended fix" : "Fix"}
        </p>
      </div>
      <div className="space-y-2.5">
        {steps.map((step, i) => (
          <div
            key={i}
            className="rounded-lg bg-card px-3 py-2.5 text-sm leading-relaxed text-foreground shadow-sm"
          >
            {renderFixStepContent(step)}
          </div>
        ))}
      </div>
      {showSocialHint && (
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          For AI-generated title, description, and OG copy, use the{" "}
          {onOpenSocialTab ? (
            <button
              type="button"
              onClick={onOpenSocialTab}
              className="font-medium text-brand hover:underline"
            >
              Social tab
            </button>
          ) : (
            <span className="font-medium text-foreground">Social tab</span>
          )}{" "}
          above.
        </p>
      )}
    </div>
  );
}

export function SuggestionIssueCard({
  issue,
  listIndex = 0,
  defaultOpen = true,
  pageUrl,
  knownIssues,
  screenshot,
  strategy,
  onOpenChange,
  onOpenSocialTab,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [viewerRect, setViewerRect] = useState(null);

  const resolved = useMemo(
    () => resolveAiSuggestionIssue(issue, knownIssues, listIndex),
    [issue, knownIssues, listIndex],
  );

  const lhEntry = resolved.entry;
  const displayTitle = resolved.displayTitle;
  const displayFinding = resolved.displayFinding;

  const severity = useMemo(
    () =>
      resolveIssueSeverity(
        { ...issue, title: displayTitle },
        knownIssues,
      ),
    [issue, displayTitle, knownIssues],
  );

  const useLighthouseContent =
    lhEntry && hasLighthouseIssueContent(lhEntry.key, lhEntry.issue);

  const affectedItems = useMemo(() => {
    if (useLighthouseContent || !knownIssues) {
      return (issue.affected || []).map((text) => ({ text }));
    }
    return enrichAffectedItems(
      { ...issue, title: displayTitle },
      knownIssues,
    );
  }, [issue, knownIssues, useLighthouseContent, displayTitle]);

  return (
    <>
      <div
        data-issue-card
        className={open ? issueCardOpenShellClass : issueCardCollapsedClass}
      >
        <StickyIssueHeader
          active={open}
          as="button"
          type="button"
          className={`flex w-full items-start p-4 text-left cursor-pointer transition-colors ${
            open ? "" : "hover:bg-muted/30"
          }`}
          onClick={() =>
            setOpen((v) => {
              const next = !v;
              onOpenChange?.(next);
              return next;
            })
          }
          aria-expanded={open}
        >
          <IssueSeverityIcon severity={severity} className="mt-1 mr-3" />
          <div className="flex-1 min-w-0 flex justify-between items-start gap-x-4">
            <h3 className="font-semibold text-foreground text-sm sm:text-base leading-snug line-clamp-2 min-w-0">
              {displayTitle}
            </h3>
            {displayFinding && (
              <span className="hidden sm:block text-sm text-muted-foreground font-medium shrink-0 w-1/3 text-right leading-tight">
                {displayFinding}
              </span>
            )}
          </div>
          <ChevronRight
            size={20}
            className={`ml-2 text-muted-foreground flex-shrink-0 transition-transform mt-1 ${
              open ? "rotate-90" : ""
            }`}
            aria-hidden
          />
        </StickyIssueHeader>

        {open && (
          <div className={issueCardBodyClass}>
            {displayFinding && (
              <p className="text-sm text-muted-foreground font-medium pt-3 sm:hidden">
                {displayFinding}
              </p>
            )}
            {useLighthouseContent ? (
              <PerformanceIssueContent
                issueKey={lhEntry.key}
                issue={lhEntry.issue}
                screenshot={screenshot}
                pageUrl={pageUrl}
                onThumbnailClick={setViewerRect}
                embedded
              />
            ) : (
              <AffectedList
                items={affectedItems}
                pageUrl={pageUrl}
                screenshot={screenshot}
                strategy={strategy}
                onThumbnailClick={setViewerRect}
              />
            )}
            <FixSteps
              steps={issue.fix}
              contextual={useLighthouseContent}
              issueKey={lhEntry?.key}
              issueTitle={displayTitle}
              onOpenSocialTab={onOpenSocialTab}
            />
          </div>
        )}
      </div>

      {viewerRect && (
        <ScreenshotViewer
          screenshot={screenshot}
          rect={viewerRect}
          strategy={strategy}
          onClose={() => setViewerRect(null)}
        />
      )}
    </>
  );
}

export function SuggestionsSummary({ text, stackLabel }) {
  if (!text) return null;
  return (
    <Card className="mb-4 bg-muted/20">
      <CardContent className="py-4">
        {stackLabel && (
          <p className="text-xs text-muted-foreground mb-2">
            Stack ·{" "}
            <span className="font-medium text-foreground capitalize">
              {stackLabel}
            </span>
          </p>
        )}
        <p className="text-sm text-foreground leading-relaxed">{text}</p>
      </CardContent>
    </Card>
  );
}
